import { apiClient } from './api';
import type { Quiz, Question, QuizAttempt, CreateQuizRequest, QuizAttemptSummary, QuizAttemptWithStudent, GradeQuizAttemptRequest, QuizWithLessonInfo } from '../types/quiz';

export const quizService = {
  // Create a new quiz
  createQuiz: async (data: CreateQuizRequest): Promise<Quiz> => {
    const response = await apiClient.post<Quiz>('/quizzes', data);
    return response.data as Quiz;
  },

  // Get all quizzes with lesson/module/course info
  listAllQuizzes: async (): Promise<QuizWithLessonInfo[]> => {
    const response = await apiClient.get<QuizWithLessonInfo[]>('/quizzes');
    return response.data as QuizWithLessonInfo[];
  },

  // Get quiz by ID
  getQuiz: async (id: string): Promise<Quiz> => {
    const response = await apiClient.get<Quiz>(`/quizzes/${id}`);
    return response.data as Quiz;
  },

  // Get quizzes by lesson
  getQuizzesByLesson: async (lessonId: string): Promise<Quiz[]> => {
    const response = await apiClient.get<Quiz[]>(`/lessons/${lessonId}/quizzes`);
    return response.data as Quiz[];
  },

  // Update quiz
  updateQuiz: async (id: string, data: Partial<CreateQuizRequest>): Promise<Quiz> => {
    const response = await apiClient.put<Quiz>(`/quizzes/${id}`, data);
    return response.data as Quiz;
  },

  // Delete quiz
  deleteQuiz: async (id: string): Promise<void> => {
    await apiClient.delete<void>(`/quizzes/${id}`);
  },

  // Publish quiz
  publishQuiz: async (id: string): Promise<Quiz> => {
    const response = await apiClient.post<Quiz>(`/quizzes/${id}/publish`);
    return response.data as Quiz;
  },

  // Add question to quiz
  addQuestion: async (quizId: string, question: Omit<Question, 'id' | 'quizId'>): Promise<Question> => {
    const response = await apiClient.post<Question>(`/quizzes/${quizId}/questions`, question);
    return response.data as Question;
  },

  // Get quiz questions
  getQuestions: async (quizId: string): Promise<Question[]> => {
    const response = await apiClient.get<Question[]>(`/quizzes/${quizId}/questions`);
    return response.data as Question[];
  },

  // Start quiz attempt
  startQuizAttempt: async (quizId: string, enrollmentId: string): Promise<QuizAttempt> => {
    const response = await apiClient.post<QuizAttempt>(`/quizzes/${quizId}/attempts`, {
      enrollment_id: enrollmentId
    });
    return response.data as QuizAttempt;
  },

  // Submit quiz attempt
  submitQuizAttempt: async (attemptId: string, answers: Record<string, number | string>): Promise<QuizAttempt> => {
    const response = await apiClient.post<QuizAttempt>(`/quiz-attempts/${attemptId}/submit`, {
      answers
    });
    return response.data as QuizAttempt;
  },

  // Admin only - Get quiz attempts summary
  getQuizAttemptsSummary: async (quizId: string): Promise<QuizAttemptSummary[]> => {
    const response = await apiClient.get<QuizAttemptSummary[]>(`/quizzes/${quizId}/attempts-summary`);
    return response.data as QuizAttemptSummary[];
  },

  // Admin only - Reset student quiz attempts
  resetQuizAttempts: async (quizId: string, enrollmentId: string): Promise<void> => {
    await apiClient.post<void>(`/quizzes/${quizId}/reset-attempts`, {
      enrollment_id: enrollmentId
    });
  },

  // Admin + Instructor - Get all attempts for a quiz with student info
  getQuizAttempts: async (quizId: string, statusFilter?: string): Promise<QuizAttemptWithStudent[]> => {
    const params = statusFilter && statusFilter !== 'all' ? `?status=${statusFilter}` : '';
    const response = await apiClient.get<QuizAttemptWithStudent[]>(`/quizzes/${quizId}/attempts${params}`);
    return response.data as QuizAttemptWithStudent[];
  },

  // Admin + Instructor - Get attempt detail
  getQuizAttemptDetail: async (attemptId: string): Promise<QuizAttempt> => {
    const response = await apiClient.get<QuizAttempt>(`/quiz-attempts/${attemptId}/detail`);
    return response.data as QuizAttempt;
  },

  // Admin + Instructor - Grade essay questions
  gradeQuizAttempt: async (attemptId: string, data: GradeQuizAttemptRequest): Promise<void> => {
    await apiClient.post<void>(`/quiz-attempts/${attemptId}/grade`, data);
  },
};
